<template>
  <div class="about-header relative overflow-hidden">
    <!-- 顶部横幅 -->
    <div class="bg-[#2f5c45] py-8 sm:py-12 relative">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 text-center relative z-10">
        <h1 class="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4">
          {{ t('about.header.title') }}
        </h1>
        <p class="text-sm sm:text-base text-gray-50 max-w-3xl mx-auto">
          {{ t('about.header.subtitle') }}
        </p>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <main class="max-w-6xl mx-auto px-8 py-16">
      <!-- 企业愿景 -->
      <section class="mb-12 sm:mb-16 lg:mb-20">
        <!-- 第一行：标题和图片 -->
         <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 mb-6 sm:mb-8">
           <div class="text-center lg:text-left order-2 lg:order-1 px-4 sm:px-0">
              <div>
                <h3 class="text-base sm:text-lg lg:text-xl font-medium text-[#2f5c45] mb-2 mt-8 sm:mt-12 hover:text-green-600 transition-colors duration-300">
                  {{ t('about.header.vision.title') }}
                </h3>
                
                <h3 class="text-base sm:text-lg lg:text-xl font-medium text-[#2f5c45] mb-2 hover:text-green-600 transition-colors duration-300">
                  {{ t('about.header.vision.subtitle') }}
                </h3>
                
                <h3 class="text-base sm:text-lg lg:text-xl font-medium text-[#2f5c45] mb-2 hover:text-green-600 transition-colors duration-300">
                  {{ t('about.header.vision.mainTitle') }}
                </h3>
              </div>
            </div>
          <div class="flex justify-center order-1 lg:order-2 px-4 sm:px-0">
            <div class="relative overflow-hidden rounded-lg shadow-2xl">
              <img 
                :src="headerImage" 
                alt="环保包装产品" 
                class="rounded-lg scale-105 max-w-full h-auto w-full sm:w-4/5 lg:w-full"
              />
            </div>
          </div>
        </div>
        <!-- 第二行：主标题和描述 -->
        <div class="px-4 sm:px-0">
          <p class="text-gray-700 text-xs sm:text-sm lg:text-base leading-relaxed max-w-4xl mx-auto lg:mx-0">
            {{ t('about.header.vision.description') }}
          </p>
        </div>
      </section>

      <!-- 创始人语录 -->
      <section class="bg-[#f8f4e9] p-8 sm:p-12 rounded-lg mb-16 sm:mb-20 lg:mb-28">
        <blockquote class="text-lg sm:text-xl text-[#6f6e69] italic leading-relaxed mb-8 sm:mb-14">
          {{ t('about.header.quote.text') }}
        </blockquote>
        <div class="text-right">
          <p class="font-medium text-[#6f6e69]">{{ t('about.header.quote.author') }}</p>
        </div>
      </section>

      <!-- 我们的客户对象 -->
      <section class="mb-12 sm:mb-16 lg:mb-20 px-4 sm:px-0">
        <h2 class="text-xl sm:text-2xl font-semibold text-green-800 mb-6 sm:mb-8 text-center lg:text-left">{{ t('about.header.clients.title') }}</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          <div
            class="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow duration-300"
          >
            <div>
              <div class="text-green-600 text-3xl sm:text-4xl mb-3 sm:mb-4">
                <i class="fas fa-leaf"></i>
              </div>
              <h3 class="text-base sm:text-lg font-medium mb-2">{{ t('about.header.clients.environmental.title') }}</h3>
              <p class="text-gray-600 text-xs sm:text-sm">{{ t('about.header.clients.environmental.description') }}</p>
            </div>
          </div>
          <div
            class="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow duration-300"
          >
            <div>
              <div class="text-green-600 text-3xl sm:text-4xl mb-3 sm:mb-4">
                <i class="fas fa-school"></i>
              </div>
              <h3 class="text-base sm:text-lg font-medium mb-2">{{ t('about.header.clients.education.title') }}</h3>
              <p class="text-gray-600 text-xs sm:text-sm">{{ t('about.header.clients.education.description') }}</p>
            </div>
          </div>
          <div
            class="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow duration-300"
          >
            <div>
              <div class="text-green-600 text-3xl sm:text-4xl mb-3 sm:mb-4">
                <i class="fas fa-building"></i>
              </div>
              <h3 class="text-base sm:text-lg font-medium mb-2">{{ t('about.header.clients.government.title') }}</h3>
              <p class="text-gray-600 text-xs sm:text-sm">{{ t('about.header.clients.government.description') }}</p>
            </div>
          </div>
          <div
            class="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow duration-300"
          >
            <div>
              <div class="text-green-600 text-3xl sm:text-4xl mb-3 sm:mb-4">
                <i class="fas fa-globe"></i>
              </div>
              <h3 class="text-base sm:text-lg font-medium mb-2">{{ t('about.header.clients.international.title') }}</h3>
              <p class="text-gray-600 text-xs sm:text-sm">{{ t('about.header.clients.international.description') }}</p>
            </div>
          </div>
        </div>
      </section>

    </main>
  </div>
</template>

<script setup>
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  
  // 导入图片资源
  import headerImage from '@/assets/about/image.png'

  const { t, locale } = useI18n()

</script>

<style scoped>
  /* 引用样式 */
  blockquote {
    position: relative;
  }

  blockquote::before {
    content: '"';
    font-size: 5rem;
    color: rgba(21, 128, 61, 0.1);
    position: absolute;
    left: -2rem;
    top: -2rem;
    z-index: 0;
  }

  blockquote::after {
    content: '"';
    font-size: 5rem;
    color: rgba(21, 128, 61, 0.1);
    position: absolute;
    right: -1rem;
    bottom: -4rem;
    z-index: 0;
  }
</style>
